import {
    buildSocialRegistrationChallengeUrl,
    createSocialRegistrationChallenge,
    ExternalRegistrationPeekResponse,
    getSocialRegistrationIssuer,
} from 'common/api/auth/socialRegistration';
import { RegistrationRequest } from 'common/api/auth/models/RegistrationRequest';
import { Organization } from 'common/api/crm/organization/enrollment/models';
import { Routes } from 'routes';

export { createSocialRegistrationChallenge };

export type SocialRegistrationProvider = 'google' | 'apple';

export type SocialRegistrationOutcome =
    | 'success'
    | 'existing_account'
    | 'missing_email'
    | 'invalid_key'
    | 'expired_key'
    | 'used_key';

export interface SocialRegistrationResumeParams {
    outcome?: string;
    provider?: string;
    externalRegistrationKey?: string;
}

const socialRegistrationStateStorageKey = 'family-enrollment-social-registration';

const socialRegistrationErrorMessages: Partial<Record<SocialRegistrationOutcome, string>> = {
    invalid_key: 'We could not finish sign-in. Please try again.',
    expired_key: 'Your sign-in session expired. Please start sign-in again.',
    used_key: 'That sign-in link has already been used. Please start sign-in again.',
};

interface PersistedFamilyEnrollmentSocialRegistrationState {
    userAccountInfo: RegistrationRequest;
    organization: Organization | null;
    enrollmentCode: string;
    codeVerifier: string;
}

export const buildFamilyEnrollmentSocialRegistrationReturnUrl = () =>
    new URL(Routes.EnrollFamily, window.location.origin).toString();

export const buildFamilyEnrollmentSocialRegistrationChallengeUrl = (
    provider: SocialRegistrationProvider,
    challenge: ReturnType<typeof createSocialRegistrationChallenge>,
    scheme?: string
) =>
    buildSocialRegistrationChallengeUrl(
        provider,
        buildFamilyEnrollmentSocialRegistrationReturnUrl(),
        getSocialRegistrationIssuer(),
        challenge,
        scheme
    );

export const exchangeFamilyEnrollmentSocialRegistrationSession = (
    externalRegistrationKey: string,
    codeVerifier: string
): Promise<ExternalRegistrationPeekResponse> =>
    fetch(`/api/local/social-registration/session`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF': '1',
        },
        body: JSON.stringify({
            externalRegistrationKey,
            codeVerifier,
        }),
    }).then(async (response) => {
        if (!response.ok) {
            throw new Error('Unable to peek external registration session.');
        }

        return response.json();
    });

export const getSocialRegistrationResumeParams = (
    search: string
): SocialRegistrationResumeParams => {
    const params = new URLSearchParams(search);

    return {
        outcome: params.get('outcome') || undefined,
        provider: params.get('provider') || undefined,
        externalRegistrationKey: params.get('externalRegistrationKey') || undefined,
    };
};

export const getSocialRegistrationErrorMessage = (outcome?: string) => {
    if (!outcome) {
        return 'We could not finish sign-in. Please try again.';
    }

    return (
        socialRegistrationErrorMessages[outcome as SocialRegistrationOutcome] ||
        'We could not finish sign-in. Please try again.'
    );
};

export const normalizeSocialRegistrationProvider = (
    provider?: string
): SocialRegistrationProvider | undefined => {
    const normalized = provider?.trim().toLowerCase();
    if (!normalized) {
        return undefined;
    }
    if (normalized.includes('google')) {
        return 'google';
    }
    if (normalized.includes('apple')) {
        return 'apple';
    }
    return undefined;
};

export const persistFamilyEnrollmentSocialRegistrationState = (
    state: PersistedFamilyEnrollmentSocialRegistrationState
) => {
    sessionStorage.setItem(socialRegistrationStateStorageKey, JSON.stringify(state));
};

export const restoreFamilyEnrollmentSocialRegistrationState = () => {
    const rawState = sessionStorage.getItem(socialRegistrationStateStorageKey);

    if (!rawState) {
        return null;
    }

    try {
        return JSON.parse(rawState) as PersistedFamilyEnrollmentSocialRegistrationState;
    } catch (_error) {
        clearFamilyEnrollmentSocialRegistrationState();
        return null;
    }
};

export const clearFamilyEnrollmentSocialRegistrationState = () => {
    sessionStorage.removeItem(socialRegistrationStateStorageKey);
};
